import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, ArrowRight, Lightbulb, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_importacao-expert/artifacts/gz8vakfs_viamundi_logo_sem_fundo_cores_originais.png";

function RichContent({ content }) {
  const lines = content.split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { i++; continue; }

    // H2 heading
    if (line.startsWith("## ")) {
      elements.push(
        <h2 key={i} className="font-heading text-2xl md:text-3xl font-bold tracking-tight mt-14 mb-6 text-foreground">
          {line.slice(3)}
        </h2>
      );
    }
    // H3 heading
    else if (line.startsWith("### ")) {
      elements.push(
        <h3 key={i} className="font-heading text-xl md:text-2xl font-bold tracking-tight mt-10 mb-4 text-foreground">
          {line.slice(4)}
        </h3>
      );
    }
    // Tip box
    else if (line.startsWith("!TIP ")) {
      elements.push(
        <div key={i} className="my-8 p-6 rounded-2xl bg-primary/5 border border-primary/20 flex gap-4">
          <Lightbulb className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold uppercase tracking-wider text-primary mb-2">Dica importante</p>
            <p className="text-base text-card-foreground leading-relaxed">{line.slice(5)}</p>
          </div>
        </div>
      );
    }
    // Alert/Important box
    else if (line.startsWith("!ALERT ")) {
      elements.push(
        <div key={i} className="my-8 p-6 rounded-2xl bg-accent/5 border border-accent/20 flex gap-4">
          <AlertCircle className="h-6 w-6 text-accent flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold uppercase tracking-wider text-accent mb-2">Atenção</p>
            <p className="text-base text-card-foreground leading-relaxed">{line.slice(7)}</p>
          </div>
        </div>
      );
    }
    // Highlight/Callout box
    else if (line.startsWith("> ")) {
      const quoteLines = [];
      while (i < lines.length && lines[i].startsWith("> ")) {
        quoteLines.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <blockquote key={`q-${i}`} className="my-8 pl-6 border-l-3 border-primary bg-card/50 py-5 pr-6 rounded-r-xl">
          {quoteLines.map((ql, qi) => (
            <p key={qi} className="text-base md:text-lg text-card-foreground leading-relaxed italic">{ql}</p>
          ))}
        </blockquote>
      );
      continue;
    }
    // Mid-article CTA
    else if (line.startsWith("!CTA")) {
      elements.push(
        <div key={i} className="my-12 p-8 rounded-2xl bg-gradient-to-r from-card to-muted border border-border/50 text-center">
          <p className="font-heading text-xl md:text-2xl font-bold tracking-tight mb-3 text-foreground">
            Precisa de apoio no seu processo aduaneiro?
          </p>
          <p className="text-muted-foreground mb-6">Fale com um especialista da Viamundi e otimize sua operação.</p>
          <a href="/#contato">
            <Button className="rounded-none px-8 py-5 font-bold uppercase tracking-widest text-sm bg-primary text-primary-foreground glow-cyan hover:scale-105 transition-transform">
              Falar com especialista <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      );
    }
    // Bullet list
    else if (line.startsWith("- ")) {
      const listItems = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        listItems.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className="my-6 space-y-3 pl-2">
          {listItems.map((item, li) => (
            <li key={li} className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-base md:text-lg text-card-foreground leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }
    // Numbered list (1. 2. 3.)
    else if (/^\d+\.\s/.test(line)) {
      const listItems = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\d+\.\s/, ""));
        i++;
      }
      elements.push(
        <ol key={`ol-${i}`} className="my-6 space-y-4 pl-2 counter-reset-item">
          {listItems.map((item, li) => (
            <li key={li} className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center">
                {li + 1}
              </span>
              <span className="text-base md:text-lg text-card-foreground leading-relaxed pt-1">{item}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }
    // Bold paragraph (starts with **)
    else if (line.startsWith("**") && line.endsWith("**")) {
      elements.push(
        <p key={i} className="text-base md:text-lg font-semibold text-foreground leading-relaxed my-5">
          {line.slice(2, -2)}
        </p>
      );
    }
    // Regular paragraph
    else {
      elements.push(
        <p key={i} className="text-base md:text-lg text-card-foreground/90 leading-[1.9] my-5">
          {line}
        </p>
      );
    }
    i++;
  }
  return <>{elements}</>;
}

export default function BlogPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blog/${id}`)
      .then(r => {
        setPost(r.data);
        document.title = `${r.data.title} | Viamundi Gestão Aduaneira`;
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    return () => { document.title = "Despacho Aduaneiro | Viamundi Aduaneira"; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="border-b border-border/30 px-6 md:px-12 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Link to="/"><img src={LOGO_URL} alt="Viamundi Gestão Aduaneira" className="h-12 w-auto object-contain" /></Link>
          </div>
        </header>
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center">
          <h1 className="font-heading text-3xl font-bold mb-4">Post não encontrado</h1>
          <p className="text-muted-foreground mb-8">O artigo que você procura não existe ou foi removido.</p>
          <Link to="/#blog">
            <Button data-testid="back-to-blog" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <ArrowLeft className="h-4 w-4 mr-2" /> Voltar ao conteúdo
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border/30 px-6 md:px-12 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="blog-logo">
            <img src={LOGO_URL} alt="Viamundi Gestão Aduaneira" className="h-12 w-auto object-contain" />
          </Link>
          <Link to="/#blog" data-testid="back-link" className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" /> Voltar
          </Link>
        </div>
      </header>

      {/* Article Hero */}
      <div className="bg-muted/20 border-b border-border/20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="flex items-center gap-3 text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 text-primary" />
            <time>{new Date(post.created_at).toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" })}</time>
            <span className="text-border">|</span>
            <span>Viamundi Gestão Aduaneira</span>
          </div>
          <h1 data-testid="blog-post-title" className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.1]">
            {post.title}
          </h1>
          <p data-testid="blog-post-excerpt" className="mt-8 text-lg md:text-xl text-muted-foreground leading-relaxed border-l-[3px] border-primary pl-6 py-1">
            {post.excerpt}
          </p>
        </div>
      </div>

      {/* Article Content */}
      <article data-testid="blog-post-article" className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <RichContent content={post.content} />

        {/* Final CTA Section */}
        <div className="mt-16 mb-8 p-10 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-card to-muted/50 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-4">
            Precisa de apoio no seu processo aduaneiro?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-lg mx-auto leading-relaxed">
            Fale com um especialista da Viamundi e descubra como otimizar sua operação de comércio exterior.
          </p>
          <a href="/#contato" data-testid="blog-final-cta">
            <Button className="rounded-none px-10 py-6 font-bold uppercase tracking-widest text-sm bg-primary text-primary-foreground glow-cyan hover:scale-105 transition-transform">
              Solicitar Consultoria <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between pt-8 border-t border-border/20">
          <Link to="/#blog">
            <Button data-testid="back-to-blog-bottom" variant="ghost" className="text-muted-foreground hover:text-primary">
              <ArrowLeft className="h-4 w-4 mr-2" /> Voltar ao conteúdo
            </Button>
          </Link>
          <a href="https://wa.me/5527988333380" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-[#25D366] transition-colors flex items-center gap-2">
            Falar via WhatsApp
          </a>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-border/30 py-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Viamundi" className="h-10 w-auto object-contain" />
            <p className="text-sm text-muted-foreground">2026 Viamundi Aduaneira - viamundiaduaneira.com.br</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
